import React, { useState, useEffect } from 'react';
import { ArrowLeft, AlertCircle, Clock, CheckCircle, XCircle, AlertTriangle, Wallet, ChevronRight, RefreshCw, DollarSign, Shield, Calendar, Send } from 'lucide-react';
import axios from 'axios';

const CashoutView = ({ setCurrentView, userDetails, token, calculateTotalBalance, setToastNotification }) => {
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasInvestments, setHasInvestments] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [requiresProof, setRequiresProof] = useState(false);

  useEffect(() => {
    Promise.all([
      checkInvestments(),
      checkProofRequirement()
    ]).finally(() => setIsLoading(false));
  }, []);

  const checkProofRequirement = async () => {
    try {
      const response = await axios.get(
        `https://api.globallequityfinance.com/api/withdrawals/proof-requirement/${userDetails.email}`,
        {
          headers: { 'Authorization': token }
        }
      );
      setRequiresProof(response.data.requiresProof);
    } catch (error) {
      console.error('Error checking proof requirement:', error);
      setToastNotification({ 
        message: 'Failed to check proof requirement', 
        type: 'error' 
      });
    }
  };

  const checkInvestments = async () => {
    try {
      const response = await axios.get(
        `https://api.globallequityfinance.com/api/investments/user/${userDetails.email}`,
        {
          headers: { 'Authorization': token }
        }
      );
      setHasInvestments(response.data.length > 0);
    } catch (error) {
      console.error('Error checking investments:', error);
      setToastNotification({ 
        message: 'Failed to check investment status', 
        type: 'error' 
      });
    }
  };

  const handleWithdrawal = async () => {
    if (requiresProof) {
      setToastNotification({ 
        message: 'You must submit a proof for your previous withdrawal before making a new request.', 
        type: 'error' 
      });
      return;
    }
    if (!hasInvestments) {
      setToastNotification({ 
        message: 'You need to have at least one investment before making a withdrawal.', 
        type: 'error' 
      });
      return;
    }

    setIsProcessing(true);

    try {
      const response = await axios.post(
        'https://api.globallequityfinance.com/api/withdrawals/request',
        {
          email: userDetails.email,
          amount: withdrawAmount
        },
        {
          headers: { 'Authorization': token }
        }
      );

      setToastNotification({ 
        message: 'Withdrawal request submitted successfully', 
        type: 'success' 
      });
      setWithdrawAmount('');
      
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to process withdrawal request';
      setToastNotification({ message: errorMessage, type: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="ge-cashout-container">
      {isLoading ? (
        <div className="ge-loading-view">
          <div className="ge-spinner"></div>
          <p>Loading...</p>
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="ge-cashout-header">
            <h1>Withdraw</h1>
          </div>

          {/* Balance Display */}
          <div className="ge-balance-card">
            <div className="ge-balance-icon">
              <Wallet size={24} />
            </div>
            <div className="ge-balance-info">
              <span className="ge-balance-label">Available Balance</span>
              <div className="ge-balance-amount">
                <span className="ge-currency">KES</span>
                <span className="ge-amount">{calculateTotalBalance()}</span>
              </div>
            </div>
          </div>

          {/* Investment/Proof Requirements */}
          {(!hasInvestments || requiresProof) && (
            <div className="ge-warning-card">
              <div className="ge-warning-icon">
                <AlertTriangle size={24} />
              </div>
              <div className="ge-warning-content">
                {!hasInvestments ? (
                  <>
                    <h3>Investment Required</h3>
                    <p>You need at least one active investment to withdraw funds</p>
                    <button 
                      className="ge-action-btn"
                      onClick={() => setCurrentView('investments')}
                    >
                      Browse Investments
                    </button>
                  </>
                ) : (
                  <>
                    <h3>Proof Required</h3>
                    <p>Submit proof for your previous withdrawal first</p>
                    <button 
                      className="ge-action-btn"
                      onClick={() => setCurrentView('proofs')}
                    >
                      Submit Proof
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Withdrawal Form */}
          {hasInvestments && !requiresProof && (
            <div className="ge-withdraw-card">
              <div className="ge-withdraw-icon">
                <Send size={28} />
              </div>
              
              <h3>Request Withdrawal</h3>
              <p>Min: KES 100 • Fee: 10%</p>

              <div className="ge-input-wrapper">
                <span className="ge-currency-label">KES</span>
                <input
                  type="number"
                  placeholder="100"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                />
              </div>

              {withdrawAmount && (
                <div className="ge-fee-info">
                  <div className="ge-fee-row">
                    <span>Amount</span>
                    <span>KES {parseFloat(withdrawAmount).toLocaleString()}</span>
                  </div>
                  <div className="ge-fee-row">
                    <span>Fee (10%)</span>
                    <span>-KES {Math.floor(parseFloat(withdrawAmount) * 0.1).toLocaleString()}</span>
                  </div>
                  <div className="ge-fee-row total">
                    <span>You'll Receive</span>
                    <span>KES {Math.floor(parseFloat(withdrawAmount) * 0.90).toLocaleString()}</span>
                  </div>
                </div>
              )}

              <button 
                className="ge-withdraw-btn"
                onClick={handleWithdrawal}
                disabled={isProcessing || !withdrawAmount || Number(withdrawAmount) < 100}
              >
                {isProcessing ? (
                  <>
                    <div className="ge-btn-spinner"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    <span>Submit Request</span>
                  </>
                )}
              </button>
            </div>
          )}
        </>
      )}

      <style jsx>{`
        .ge-cashout-container {
          padding: 16px;
          max-width: 400px;
          margin: 0 auto;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          min-height: 100vh;
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
        }

        .ge-loading-view {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 300px;
          gap: 16px;
          color: #8892b0;
        }

        .ge-spinner, .ge-btn-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(231, 76, 60, 0.2);
          border-top: 3px solid #e74c3c;
          border-radius: 50%;
          animation: ge-spin 1s linear infinite;
        }

        .ge-btn-spinner {
          width: 20px;
          height: 20px;
          border-width: 2px;
        }

        @keyframes ge-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .ge-cashout-header {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 24px;
        }

        .ge-back-btn {
          width: 44px;
          height: 44px;
          background: rgba(23, 42, 69, 0.8);
          border: 1px solid rgba(231, 76, 60, 0.3);
          border-radius: 12px;
          color: #e74c3c;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          border: none;
        }

        .ge-back-btn:hover {
          background: rgba(231, 76, 60, 0.1);
          transform: translateY(-2px);
        }

        .ge-cashout-header h1 {
          font-size: 24px;
          font-weight: 700;
          color: white;
          margin: 0;
        }

        .ge-balance-card {
          background: rgba(23, 42, 69, 0.9);
          border: 1px solid rgba(231, 76, 60, 0.2);
          border-radius: 20px;
          padding: 24px;
          margin-bottom: 24px;
          display: flex;
          align-items: center;
          gap: 20px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
        }

        .ge-balance-icon {
          width: 56px;
          height: 56px;
          background: linear-gradient(135deg, #e74c3c, #c0392b);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          box-shadow: 0 8px 20px rgba(231, 76, 60, 0.3);
        }

        .ge-balance-info {
          flex: 1;
        }

        .ge-balance-label {
          font-size: 12px;
          color: #8892b0;
          display: block;
          margin-bottom: 6px;
          text-transform: uppercase;
          letter-spacing: 1px;
          font-weight: 500;
        }

        .ge-balance-amount {
          display: flex;
          align-items: baseline;
          gap: 8px;
        }

        .ge-balance-amount .ge-currency {
          font-size: 18px;
          font-weight: 600;
          color: #e74c3c;
        }

        .ge-balance-amount .ge-amount {
          font-size: 28px;
          font-weight: 700;
          color: white;
        }

        .ge-warning-card {
          background: linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(245, 158, 11, 0.05));
          border: 1px solid rgba(245, 158, 11, 0.3);
          border-radius: 20px;
          padding: 28px;
          margin-bottom: 24px;
          text-align: center;
        }

        .ge-warning-icon {
          width: 60px;
          height: 60px;
          background: rgba(245, 158, 11, 0.15);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #f39c12;
          margin: 0 auto 20px auto;
          box-shadow: 0 8px 20px rgba(245, 158, 11, 0.2);
        }

        .ge-warning-content h3 {
          color: white;
          margin: 0 0 12px 0;
          font-size: 20px;
          font-weight: 700;
        }

        .ge-warning-content p {
          color: #8892b0;
          margin: 0 0 20px 0;
          font-size: 14px;
          line-height: 1.5;
        }

        .ge-action-btn {
          background: linear-gradient(135deg, #f39c12, #e67e22);
          color: white;
          border: none;
          border-radius: 12px;
          padding: 14px 24px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 6px 16px rgba(243, 156, 18, 0.3);
        }

        .ge-action-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(243, 156, 18, 0.4);
        }

        .ge-withdraw-card {
          background: rgba(23, 42, 69, 0.9);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(231, 76, 60, 0.2);
          border-radius: 24px;
          padding: 32px 28px;
          text-align: center;
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
        }

        .ge-withdraw-icon {
          width: 72px;
          height: 72px;
          background: linear-gradient(135deg, #3498db, #2980b9);
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          margin: 0 auto 24px auto;
          box-shadow: 0 12px 28px rgba(52, 152, 219, 0.4);
        }

        .ge-withdraw-card h3 {
          font-size: 24px;
          font-weight: 700;
          color: white;
          margin: 0 0 8px 0;
        }

        .ge-withdraw-card p {
          color: #8892b0;
          margin: 0 0 28px 0;
          font-size: 14px;
        }

        .ge-input-wrapper {
          display: flex;
          background: rgba(16, 20, 31, 0.8);
          border: 2px solid rgba(231, 76, 60, 0.2);
          border-radius: 16px;
          overflow: hidden;
          margin-bottom: 20px;
          transition: all 0.3s ease;
        }

        .ge-input-wrapper:focus-within {
          border-color: #e74c3c;
          box-shadow: 0 0 0 4px rgba(231, 76, 60, 0.1);
          transform: translateY(-2px);
        }

        .ge-currency-label {
          background: rgba(231, 76, 60, 0.1);
          color: #e74c3c;
          padding: 20px 24px;
          font-weight: 700;
          font-size: 16px;
          border-right: 1px solid rgba(231, 76, 60, 0.2);
        }

        .ge-input-wrapper input {
          flex: 1;
          background: transparent;
          border: none;
          padding: 20px 24px;
          color: white;
          font-size: 20px;
          font-weight: 600;
          outline: none;
        }

        .ge-input-wrapper input::placeholder {
          color: #8892b0;
        }

        .ge-fee-info {
          background: rgba(16, 20, 31, 0.6);
          border-radius: 16px;
          padding: 20px;
          margin-bottom: 24px;
          border: 1px solid rgba(231, 76, 60, 0.1);
        }

        .ge-fee-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          font-size: 14px;
          color: #8892b0;
        }

        .ge-fee-row:last-child {
          margin-bottom: 0;
        }

        .ge-fee-row.total {
          padding-top: 12px;
          border-top: 1px solid rgba(231, 76, 60, 0.2);
          font-weight: 700;
          color: white;
        }

        .ge-fee-row.total span:last-child {
          color: #2ecc71;
          font-size: 16px;
        }

        .ge-withdraw-btn {
          width: 100%;
          background: linear-gradient(135deg, #3498db, #2980b9);
          color: white;
          border: none;
          border-radius: 16px;
          padding: 20px;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 8px 24px rgba(52, 152, 219, 0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
        }

        .ge-withdraw-btn:hover:not(:disabled) {
          transform: translateY(-3px);
          box-shadow: 0 12px 32px rgba(52, 152, 219, 0.4);
        }

        .ge-withdraw-btn:disabled {
          background: rgba(136, 146, 176, 0.2);
          color: rgba(136, 146, 176, 0.6);
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        /* Mobile responsiveness */
        @media (max-width: 480px) {
          .ge-cashout-container {
            padding: 12px;
          }

          .ge-cashout-header h1 {
            font-size: 20px;
          }

          .ge-balance-card {
            padding: 20px;
            gap: 16px;
          }

          .ge-balance-icon {
            width: 48px;
            height: 48px;
          }

          .ge-balance-amount .ge-amount {
            font-size: 24px;
          }

          .ge-withdraw-card {
            padding: 24px 20px;
          }

          .ge-withdraw-icon {
            width: 60px;
            height: 60px;
          }

          .ge-withdraw-card h3 {
            font-size: 20px;
          }

          .ge-warning-card {
            padding: 24px 20px;
          }

          .ge-warning-icon {
            width: 52px;
            height: 52px;
          }
        }

        @media (max-width: 360px) {
          .ge-currency-label {
            padding: 18px 20px;
            font-size: 14px;
          }

          .ge-input-wrapper input {
            padding: 18px 20px;
            font-size: 18px;
          }

          .ge-withdraw-btn {
            padding: 18px;
            font-size: 15px;
          }
        }
      `}</style>
    </div>
  );
};

export default CashoutView;